import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Box,
  Switch,
} from '@mui/material'
import {
  adminOrderService,
  productService,
  purchaseCatalogService,
} from '../services'
import useAuthStore from '../store/authStore'
import { canAddProductOnShipment } from '../utils/adminPermissions'
import { getArticleCode } from '../utils/articleCode'
import {
  collectNonPurchasableShipmentItems,
  getLineRemainingToShip,
  lineRequiresStockAcknowledgement,
  resolveShipmentLineNotice,
  shipmentAcknowledgeOptions,
  shipmentAcknowledgementKey,
  shipmentAcknowledgementLabel,
  shipmentLineNoticeSeverity,
} from '../utils/shipmentAvailabilityWarning'
import NonPurchasableShipmentBanner from './NonPurchasableShipmentBanner'
import { getLineMrp, getLineSellingPriceTp } from '../utils/purchaseOrderLinePricing'
import {
  getPurchaseCatalogCostPrice,
  getPurchaseCatalogMrpPrice,
} from '../utils/purchaseCatalogPricing'
import {
  formatSellingPriceExceedsMrpError,
  sellingPriceExceedsMrp,
  validatePositiveSellingPriceAndMrp,
} from '../utils/sellingPriceMrp'
import { parseShipmentError } from '../utils/shipmentErrors'
import { normalizeManualSalesOrder } from '../utils/manualSalesOrder'
import ManualSoNumberFields from './ManualSoNumberFields'
import {
  applyShipmentExcelUpdatesExclusive,
  buildExcelOnlyPendingAdds,
} from '../utils/shipmentExcelImport'
import ShipmentReferenceFields, {
  shipmentReferencePayload,
} from './ShipmentReferenceFields'
import ShipmentExcelImportPanel from './ShipmentExcelImportPanel'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import DsdShipmentDialog from './DsdShipmentDialog'
import ShipmentVatReviewDialog from './ShipmentVatReviewDialog'
import {
  DistributionCenterField,
  usePaginatedLocationSearch,
} from './shipmentLocationPicker'
import {
  buildDsdByProductIdFromLines,
  collectPositiveDsdSelections,
  getOrderLineProductId,
  isExternalProduct,
  isProductDsdEnabled,
  zeroDsdQuantities,
} from '../utils/dsdShipment'
import { buildShipmentVatReviewRows } from '../utils/shipmentVatReview'

const EMPTY_REFERENCE_FIELDS = {
  dn_reference: '',
  tpn_number: '',
  invoice_number: '',
}

function normalizeShipQty(raw) {
  if (raw === '' || raw === '-') return raw
  const num = Number(raw)
  if (!Number.isFinite(num)) return '0'
  return String(Math.max(0, Math.floor(num)))
}

function buildInitialQtyByLine(lines) {
  const initial = {}
  for (const li of lines) {
    const remaining = getLineRemainingToShip(li)
    initial[li.id] = String(remaining > 0 ? remaining : 0)
  }
  return initial
}

function mapAvailabilityRows(rows) {
  const map = {}
  for (const row of rows || []) {
    if (row?.order_line_item_id != null) {
      map[row.order_line_item_id] = row
    }
  }
  return map
}

function formatMoney(value) {
  return `৳${Number(value).toFixed(2)}`
}

function ShipmentLineRow({
  lineItem,
  qty,
  locationId,
  availabilityLoading,
  availRow,
  onQtyChange,
  onQtyBlur,
  dsdEnabled = false,
  dsdSaving = false,
  onDsdToggle,
}) {
  const remaining = getLineRemainingToShip(lineItem)
  const shipNow = Number(qty)
  const overMax = Number.isFinite(shipNow) && shipNow > remaining
  const articleCode = getArticleCode(lineItem)
  const available =
    locationId && availRow != null
      ? Number(availRow.available_quantity) || 0
      : null
  const lineNotice =
    locationId && availRow != null
      ? resolveShipmentLineNotice(availRow, shipNow)
      : null
  const noticeSeverity =
    locationId && availRow != null
      ? shipmentLineNoticeSeverity(availRow, shipNow)
      : null
  const mrp = getLineMrp(lineItem)

  return (
    <TableRow>
      <TableCell>
        {lineItem.product_name || lineItem.id}
        {articleCode && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5 }}
          >
            Article Code: {articleCode}
          </Typography>
        )}
        {lineNotice && (
          <Typography
            variant="caption"
            color={noticeSeverity === 'error' ? 'error.main' : 'warning.main'}
            display="block"
            sx={{ mt: 0.75 }}
          >
            {lineNotice}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">{lineItem.quantity || 0}</TableCell>
      <TableCell align="right">{lineItem.shipped_quantity ?? 0}</TableCell>
      <TableCell align="right">{remaining}</TableCell>
      <TableCell align="right">
        {!locationId
          ? '—'
          : availabilityLoading
            ? '…'
            : available != null
              ? available
              : '—'}
      </TableCell>
      <TableCell align="right">
        {mrp != null ? formatMoney(mrp) : '—'}
      </TableCell>
      <TableCell align="right">
        {formatMoney(getLineSellingPriceTp(lineItem))}
      </TableCell>
      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Switch
              size="small"
              checked={dsdEnabled === true}
              disabled={
                dsdSaving ||
                isExternalProduct(lineItem) ||
                typeof onDsdToggle !== 'function'
              }
              onChange={(e) => onDsdToggle?.(e.target.checked)}
              inputProps={{ 'aria-label': `DSD for ${lineItem.product_name || lineItem.id}` }}
            />
          }
          label={
            dsdSaving ? (
              <CircularProgress size={14} />
            ) : dsdEnabled ? (
              'Yes'
            ) : (
              'No'
            )
          }
        />
      </TableCell>
      <TableCell
        align="right"
        sx={{ width: 112, minWidth: 112, whiteSpace: 'nowrap' }}
      >
        <TextField
          size="small"
          type="number"
          sx={{ width: '100%', minWidth: 96 }}
          inputProps={{ min: 0, step: 1 }}
          value={qty ?? ''}
          onChange={(e) => onQtyChange(normalizeShipQty(e.target.value))}
          onBlur={onQtyBlur}
          error={overMax}
          helperText={
            overMax
              ? `Above remaining (${remaining}) — order qty will increase`
              : undefined
          }
          disabled={false}
        />
      </TableCell>
    </TableRow>
  )
}

function PendingAddRow({
  add,
  locationId,
  availabilityLoading,
  availRow,
  submitting,
  onQtyChange,
  onSellingPriceChange,
  onRemove,
}) {
  const shipNow = Number(add.quantity) || 0
  const mrp = Number(add.mrp)
  const available =
    locationId && availRow != null
      ? Number(availRow.available_quantity) || 0
      : null
  const lineNotice =
    locationId && availRow != null
      ? resolveShipmentLineNotice(availRow, shipNow)
      : null
  const noticeSeverity =
    locationId && availRow != null
      ? shipmentLineNoticeSeverity(availRow, shipNow)
      : null
  const sellingExceedsMrp = sellingPriceExceedsMrp(add.unit_price, add.mrp)

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {add.product_name}
            {add.sku ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                Article Code: {add.sku}
              </Typography>
            ) : null}
            <Typography
              variant="caption"
              color="primary.main"
              display="block"
              sx={{ mt: 0.5 }}
            >
              Added to this shipment
            </Typography>
            {lineNotice && (
              <Typography
                variant="caption"
                color={
                  noticeSeverity === 'error' ? 'error.main' : 'warning.main'
                }
                display="block"
                sx={{ mt: 0.75 }}
              >
                {lineNotice}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            disabled={submitting}
            onClick={onRemove}
            aria-label="Remove added product"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell align="right">0</TableCell>
      <TableCell align="right">0</TableCell>
      <TableCell align="right">{shipNow}</TableCell>
      <TableCell align="right">
        {!locationId
          ? '—'
          : availabilityLoading
            ? '…'
            : available != null
              ? available
              : '—'}
      </TableCell>
      <TableCell align="right">
        {Number.isFinite(mrp) ? formatMoney(mrp) : '—'}
      </TableCell>
      <TableCell align="right">
        <TextField
          size="small"
          type="number"
          sx={{ width: 100 }}
          inputProps={{ min: 0, step: 0.01 }}
          value={add.unit_price}
          disabled={submitting}
          error={sellingExceedsMrp}
          helperText={
            sellingExceedsMrp
              ? formatSellingPriceExceedsMrpError(add.unit_price, add.mrp)
              : undefined
          }
          onChange={(e) => onSellingPriceChange(e.target.value)}
        />
      </TableCell>
      <TableCell align="center">
        {add.dsd === true ? 'Yes' : 'No'}
      </TableCell>
      <TableCell
        align="right"
        sx={{ width: 112, minWidth: 112, whiteSpace: 'nowrap' }}
      >
        <TextField
          size="small"
          type="number"
          sx={{ width: '100%', minWidth: 96 }}
          inputProps={{ min: 0, step: 1 }}
          value={add.quantity}
          disabled={submitting}
          onChange={(e) => onQtyChange(normalizeShipQty(e.target.value))}
        />
      </TableCell>
    </TableRow>
  )
}

const CreateShipmentModal = ({
  open,
  onClose,
  order,
  onSuccess,
  /** Soft-update parent order line `dsd` without a full page reload. */
  onProductDsdUpdated,
  /** Soft-update parent order line `vat` without a full page reload. */
  onProductVatUpdated,
  title = 'Fulfillment Center Assign',
  canEnterManualSo = false,
  /**
   * TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
   * Backend org flag; when false hide SO/DSD and use local-only create.
   * After rollout, delete this prop and always show the integrated UI.
   */
  externalIntegrationEnabled = false,
}) => {
  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const canAddCatalogProduct = canAddProductOnShipment(
    adminRoleCodes,
    isSuperuserActor,
  )
  const [qtyByLine, setQtyByLine] = useState({})
  const [referenceFields, setReferenceFields] = useState(EMPTY_REFERENCE_FIELDS)
  const [soNumber, setSoNumber] = useState('')
  const [soReason, setSoReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [availabilityByLine, setAvailabilityByLine] = useState({})
  const [availabilityByProduct, setAvailabilityByProduct] = useState({})
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [hasMissingLots, setHasMissingLots] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  // SCRUM-760: products not on the original order (ops roles / superuser only)
  const [pendingAdds, setPendingAdds] = useState([])
  const [listPage, setListPage] = useState(0)
  const [listRowsPerPage, setListRowsPerPage] = useState(10)
  const [listFilter, setListFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 400)
  const [searchOptions, setSearchOptions] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [dsdByProductId, setDsdByProductId] = useState({})
  const [dsdSavingProductId, setDsdSavingProductId] = useState(null)
  const [dsdConfirmOpen, setDsdConfirmOpen] = useState(false)
  const [dsdDialogOpen, setDsdDialogOpen] = useState(false)
  const [pendingSubmitOverrides, setPendingSubmitOverrides] = useState(null)
  const [vatReviewOpen, setVatReviewOpen] = useState(false)
  const [vatReviewRows, setVatReviewRows] = useState([])
  const [vatReviewError, setVatReviewError] = useState(null)
  const [pendingVatSubmit, setPendingVatSubmit] = useState(null)

  const lines = order?.order_line_items || []
  const orgId = order?.org_id ?? order?.organization?.id ?? null

  const clearAvailability = useCallback(() => {
    setAvailabilityByLine({})
    setAvailabilityByProduct({})
    setHasMissingLots(false)
    setAcknowledged(false)
  }, [])

  const locationPicker = usePaginatedLocationSearch({
    open,
    onError: setError,
  })

  const {
    options: locations,
    selected: selectedLocation,
    locationId,
    inputValue: locationInputValue,
    searchQuery: locationSearchQuery,
    loading: locationsLoading,
    loadingMore: locationsLoadingMore,
    resetSelection: resetLocationSelection,
    selectLocation,
    handleInputChange: handleLocationInputChange,
    handleListScroll: handleLocationListScroll,
  } = locationPicker

  useEffect(() => {
    if (!open || !lines.length) return
    setQtyByLine(buildInitialQtyByLine(lines))
    setReferenceFields({ ...EMPTY_REFERENCE_FIELDS })
    setSoNumber('')
    setSoReason('')
    setError(null)
    setPendingAdds([])
    setListPage(0)
    setListFilter('')
    setSearchInput('')
    setSearchOptions([])
    setSelectedProduct(null)
    setDsdByProductId(buildDsdByProductIdFromLines(lines))
    setDsdSavingProductId(null)
    setDsdConfirmOpen(false)
    setDsdDialogOpen(false)
    setPendingSubmitOverrides(null)
    setVatReviewOpen(false)
    setVatReviewRows([])
    setVatReviewError(null)
    setPendingVatSubmit(null)
    resetLocationSelection()
    clearAvailability()
  }, [
    open,
    order?.order_id,
    lines.length,
    resetLocationSelection,
    clearAvailability,
  ])

  useEffect(() => {
    if (!open || !orgId || !canAddCatalogProduct) {
      setSearchOptions([])
      return
    }
    const q = String(debouncedSearch || '').trim()
    if (!q) {
      setSearchOptions([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setSearchLoading(true)
        const res = await purchaseCatalogService.searchCatalog(orgId, {
          page: 1,
          limit: 20,
          q,
        })
        if (!cancelled) {
          setSearchOptions(Array.isArray(res?.data) ? res.data : [])
        }
      } catch {
        if (!cancelled) setSearchOptions([])
      } finally {
        if (!cancelled) setSearchLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, orgId, debouncedSearch, canAddCatalogProduct])

  useEffect(() => {
    if (!canAddCatalogProduct && pendingAdds.length > 0) {
      setPendingAdds([])
    }
  }, [canAddCatalogProduct, pendingAdds.length])

  const existingProductIds = useMemo(() => {
    const ids = new Set()
    for (const li of lines) {
      const id = String(
        li?.admin_product_id ||
          li?.product_id ||
          li?.adminProduct?.id ||
          '',
      ).trim()
      if (id) ids.add(id)
    }
    for (const add of pendingAdds) {
      if (add.admin_product_id) ids.add(String(add.admin_product_id))
    }
    return ids
  }, [lines, pendingAdds])

  const tryAddCatalogProduct = useCallback(
    (product) => {
      if (!canAddCatalogProduct) {
        setError(
          'Only Operations Executive, Operations Manager, or Superuser can add products not on the original order.',
        )
        return
      }
      const id = String(product?.admin_product_id || product?.id || '').trim()
      if (!id) return
      if (existingProductIds.has(id)) {
        setError(
          'That product is already on the order or already added — increase ship qty on its line instead.',
        )
        setSelectedProduct(null)
        setSearchInput('')
        return
      }
      const sellingPrice = Number(
        product.selling_price_tp ??
          getPurchaseCatalogCostPrice(product) ??
          product.price ??
          0,
      )
      const mrp = Number(getPurchaseCatalogMrpPrice(product))
      setDsdByProductId((prev) => ({
        ...prev,
        [id]: product.dsd === true,
      }))
      const vatRaw = product.vat
      const vatNum =
        vatRaw === null || vatRaw === undefined || vatRaw === ''
          ? null
          : Number(vatRaw)
      setPendingAdds((prev) => [
        {
          key: `${id}-${Date.now()}`,
          admin_product_id: id,
          product_name: product.product_name || product.name || 'Product',
          sku: getArticleCode(product) || product.sku || '',
          quantity: '1',
          dsd: product.dsd === true,
          // Catalog search may coerce null→0; VAT review refreshes from product API.
          vat: Number.isFinite(vatNum) ? vatNum : null,
          source: product.source || product.product_source || 'shwapno',
          unit_price: String(Number.isFinite(sellingPrice) ? sellingPrice : 0),
          mrp: String(
            Number.isFinite(mrp) && mrp > 0
              ? mrp
              : Number.isFinite(sellingPrice)
                ? sellingPrice
                : 0,
          ),
        },
        ...prev,
      ])
      setListPage(0)
      setSelectedProduct(null)
      setSearchInput('')
      setError(null)
    },
    [canAddCatalogProduct, existingProductIds],
  )
  const pendingProductIdsKey = useMemo(
    () =>
      pendingAdds
        .map((a) => String(a.admin_product_id || '').trim())
        .filter(Boolean)
        .sort()
        .join(','),
    [pendingAdds],
  )

  // Newest catalog adds first, then existing lines by newest id.
  const productListRows = useMemo(() => {
    const existing = [...lines].sort(
      (a, b) => (Number(b.id) || 0) - (Number(a.id) || 0),
    )
    return [
      ...pendingAdds.map((add) => ({ kind: 'pending', add })),
      ...existing.map((li) => ({ kind: 'existing', li })),
    ]
  }, [lines, pendingAdds])

  const filteredProductRows = useMemo(() => {
    const q = String(listFilter || '').trim().toLowerCase()
    if (!q) return productListRows
    return productListRows.filter((entry) => {
      if (entry.kind === 'pending') {
        const code = String(entry.add?.sku || '').toLowerCase()
        const name = String(entry.add?.product_name || '').toLowerCase()
        return code.includes(q) || name.includes(q)
      }
      const code = String(getArticleCode(entry.li) || '').toLowerCase()
      const name = String(
        entry.li?.product_name || entry.li?.product?.product_name || '',
      ).toLowerCase()
      return code.includes(q) || name.includes(q)
    })
  }, [productListRows, listFilter])

  const pagedProductRows = useMemo(() => {
    const start = listPage * listRowsPerPage
    return filteredProductRows.slice(start, start + listRowsPerPage)
  }, [filteredProductRows, listPage, listRowsPerPage])

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredProductRows.length / listRowsPerPage) - 1,
    )
    if (listPage > maxPage) setListPage(maxPage)
  }, [filteredProductRows.length, listPage, listRowsPerPage])

  useEffect(() => {
    if (!open || !locationId || !order?.order_id) {
      clearAvailability()
      setAvailabilityLoading(false)
      return
    }

    let cancelled = false
    const load = async () => {
      setAvailabilityLoading(true)
      try {
        const productIds = pendingProductIdsKey
          ? pendingProductIdsKey.split(',')
          : []
        const res = await adminOrderService.getShipmentAvailability(
          order.order_id,
          locationId,
          productIds,
        )
        if (cancelled) return
        const payload = res?.data || res
        setAvailabilityByLine(mapAvailabilityRows(payload?.lines))
        const byProduct = {}
        for (const row of payload?.extra_products || []) {
          if (row?.admin_product_id) {
            byProduct[String(row.admin_product_id)] = row
          }
        }
        setAvailabilityByProduct(byProduct)
        setHasMissingLots(!!payload?.has_missing_lots)
        setAcknowledged(false)
      } catch {
        if (!cancelled) clearAvailability()
      } finally {
        if (!cancelled) setAvailabilityLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [
    open,
    locationId,
    order?.order_id,
    pendingProductIdsKey,
    clearAvailability,
  ])

  const missingLotLines = useMemo(
    () =>
      lines.filter((li) => {
        const row = availabilityByLine[li.id]
        return row?.will_auto_provision && (Number(qtyByLine[li.id]) || 0) > 0
      }),
    [lines, availabilityByLine, qtyByLine],
  )

  const missingLotPendingAdds = useMemo(
    () =>
      pendingAdds.filter((add) => {
        const row = availabilityByProduct[String(add.admin_product_id)]
        return row?.will_auto_provision && (Number(add.quantity) || 0) > 0
      }),
    [pendingAdds, availabilityByProduct],
  )

  const hasQtyOverRemaining = useMemo(
    () =>
      lines.some((li) => {
        const remaining = getLineRemainingToShip(li)
        const qty = Number(qtyByLine[li.id])
        return Number.isFinite(qty) && qty > remaining
      }),
    [lines, qtyByLine],
  )

  const showNoLocationsWarning =
    !locationsLoading &&
    locations.length === 0 &&
    !locationSearchQuery &&
    !locationId

  const needsAutoProvisionAck =
    hasMissingLots || missingLotPendingAdds.length > 0

  const needsStockAck = useMemo(() => {
    if (!locationId || availabilityLoading) return false
    const lineShort = lines.some((li) =>
      lineRequiresStockAcknowledgement(
        availabilityByLine[li.id],
        qtyByLine[li.id],
      ),
    )
    const addShort = pendingAdds.some((add) =>
      lineRequiresStockAcknowledgement(
        availabilityByProduct[String(add.admin_product_id)],
        add.quantity,
      ),
    )
    return lineShort || addShort
  }, [
    locationId,
    availabilityLoading,
    lines,
    availabilityByLine,
    qtyByLine,
    pendingAdds,
    availabilityByProduct,
  ])

  const nonPurchasableItems = useMemo(() => {
    if (!locationId || availabilityLoading) return []
    return collectNonPurchasableShipmentItems({
      lines,
      qtyByLine,
      availabilityByLine,
      pendingAdds,
      availabilityByProduct,
      getArticleCode,
    })
  }, [
    locationId,
    availabilityLoading,
    lines,
    qtyByLine,
    availabilityByLine,
    pendingAdds,
    availabilityByProduct,
  ])

  const hasNonPurchasableLine = nonPurchasableItems.length > 0

  const needsAcknowledgement = needsAutoProvisionAck || needsStockAck
  const acknowledgementLabel = shipmentAcknowledgementLabel({
    needsStockAck,
    needsAutoProvisionAck,
  })
  const acknowledgementKey = useMemo(
    () =>
      shipmentAcknowledgementKey({
        locationId,
        quantities: [
          ...lines.map((li) => ({
            id: li.id,
            qty: String(qtyByLine[li.id] ?? ''),
          })),
          ...pendingAdds.map((add) => ({
            id: add.key,
            qty: String(add.quantity ?? ''),
          })),
        ],
        needsStockAck,
        needsAutoProvisionAck,
      }),
    [
      locationId,
      lines,
      qtyByLine,
      pendingAdds,
      needsStockAck,
      needsAutoProvisionAck,
    ],
  )

  useEffect(() => {
    setAcknowledged(false)
  }, [acknowledgementKey])

  // TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
  const allowManualSo = canEnterManualSo && externalIntegrationEnabled === true
  const manualSo = allowManualSo
    ? normalizeManualSalesOrder(soNumber, soReason)
    : { soNumber: '', reason: '', error: null, active: false }

  const canSubmit =
    !submitting &&
    !!locationId &&
    !hasNonPurchasableLine &&
    !(needsAcknowledgement && !acknowledged) &&
    !manualSo.error

  const handleDsdToggle = useCallback(
    async (lineItem, nextDsd) => {
      const productId = getOrderLineProductId(lineItem)
      if (!productId) {
        setError('This line is missing a product id, so DSD cannot be updated.')
        return
      }
      if (isExternalProduct(lineItem) && nextDsd) {
        setError('External products cannot be marked as DSD.')
        return
      }
      if (dsdSavingProductId) return
      const previous = isProductDsdEnabled(lineItem, dsdByProductId)
      setDsdSavingProductId(productId)
      setError(null)
      // Optimistic UI for all lines sharing the product; revert on failure.
      setDsdByProductId((prev) => ({ ...prev, [productId]: nextDsd === true }))
      try {
        await productService.updateProduct(productId, { dsd: nextDsd === true })
        // Patch parent order lines in place — do not refetch / remount the page.
        onProductDsdUpdated?.(productId, nextDsd === true)
      } catch (err) {
        setDsdByProductId((prev) => ({ ...prev, [productId]: previous }))
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Could not update product DSD setting',
        )
      } finally {
        setDsdSavingProductId(null)
      }
    },
    [dsdByProductId, dsdSavingProductId, onProductDsdUpdated],
  )

  const handleQtyChange = useCallback((lineId, next) => {
    setQtyByLine((prev) => ({ ...prev, [lineId]: next }))
  }, [])

  const handleQtyBlur = useCallback((lineId) => {
    setQtyByLine((prev) => {
      const raw = prev[lineId]
      if (raw === '' || raw === '-') {
        return { ...prev, [lineId]: '0' }
      }
      return prev
    })
  }, [])

  const handleRemoveAllNonPurchasable = useCallback(() => {
    const blocked = collectNonPurchasableShipmentItems({
      lines,
      qtyByLine,
      availabilityByLine,
      pendingAdds,
      availabilityByProduct,
      getArticleCode,
    })
    if (!blocked.length) return

    const lineIds = new Set(
      blocked.filter((i) => i.kind === 'line').map((i) => i.lineId),
    )
    const pendingKeys = new Set(
      blocked.filter((i) => i.kind === 'pending').map((i) => i.pendingKey),
    )

    if (lineIds.size > 0) {
      setQtyByLine((prev) => {
        const next = { ...prev }
        for (const id of lineIds) {
          next[id] = '0'
        }
        return next
      })
    }
    if (pendingKeys.size > 0) {
      setPendingAdds((prev) => prev.filter((row) => !pendingKeys.has(row.key)))
    }
    setError(null)
  }, [
    lines,
    qtyByLine,
    availabilityByLine,
    pendingAdds,
    availabilityByProduct,
  ])

  const handleFocusNonPurchasableItem = useCallback((item) => {
    const term = String(item?.articleCode || item?.productName || '').trim()
    if (!term) return
    setListFilter(term)
    setListPage(0)
  }, [])

  const handleSubmit = useCallback(
    async (qtyOverride = null, pendingAddsOverride = null, options = {}) => {
      if (!order?.order_id) return false
      if (submitting) return false

      // Normal Ship: qtyOverride/pendingAddsOverride are null → use modal draft.
      // Excel confirm: passes exclusive maps built only from the preview.
      const effectiveQtyByLine = qtyOverride || qtyByLine
      const effectivePendingAdds =
        pendingAddsOverride != null ? pendingAddsOverride : pendingAdds
      const skipDsdGate = options.skipDsdGate === true

      if (!locationId) {
        setError('Select a distribution center before creating the shipment.')
        return false
      }

      // TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
      // Keep DSD gate permanently; remove only the externalIntegrationEnabled conjunct.
      if (externalIntegrationEnabled && !skipDsdGate) {
        const dsdSelection = collectPositiveDsdSelections({
          lines,
          qtyByLine: effectiveQtyByLine,
          pendingAdds: effectivePendingAdds,
          dsdByProductId,
        })
        if (dsdSelection.hasDsd) {
          setPendingSubmitOverrides({
            qtyByLine: effectiveQtyByLine,
            pendingAdds: effectivePendingAdds,
          })
          setDsdConfirmOpen(true)
          return false
        }
      }

      const blockedNonPurchasable =
        !!locationId &&
        !availabilityLoading &&
        collectNonPurchasableShipmentItems({
          lines,
          qtyByLine: effectiveQtyByLine,
          availabilityByLine,
          pendingAdds: effectivePendingAdds,
          availabilityByProduct,
          getArticleCode,
        }).length > 0

      if (blockedNonPurchasable) {
        setError(
          'Remove or resolve non-purchasable articles before creating the shipment.',
        )
        return false
      }

      // Recompute ack gates against effective quantities (Excel confirm may
      // differ from the manual draft still shown in the table).
      const effectiveNeedsAutoProvisionAck =
        hasMissingLots ||
        effectivePendingAdds.some((add) => {
          const row = availabilityByProduct[String(add.admin_product_id)]
          return row?.will_auto_provision && (Number(add.quantity) || 0) > 0
        }) ||
        lines.some((li) => {
          const row = availabilityByLine[li.id]
          return (
            row?.will_auto_provision &&
            (Number(effectiveQtyByLine[li.id]) || 0) > 0
          )
        })
      const effectiveNeedsStockAck =
        !!locationId &&
        !availabilityLoading &&
        (lines.some((li) =>
          lineRequiresStockAcknowledgement(
            availabilityByLine[li.id],
            effectiveQtyByLine[li.id],
          ),
        ) ||
          effectivePendingAdds.some((add) =>
            lineRequiresStockAcknowledgement(
              availabilityByProduct[String(add.admin_product_id)],
              add.quantity,
            ),
          ))
      const effectiveNeedsAcknowledgement =
        effectiveNeedsAutoProvisionAck || effectiveNeedsStockAck

      if (effectiveNeedsAcknowledgement && !acknowledged) {
        setError('Confirm that you understand before creating the shipment.')
        return false
      }

      const skipVatReview = options.skipVatReview === true
      // TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
      // Keep VAT review for integrated shops; remove only the flag conjunct.
      if (externalIntegrationEnabled && !skipVatReview) {
        const reviewRows = buildShipmentVatReviewRows({
          lines,
          qtyByLine: effectiveQtyByLine,
          pendingAdds: effectivePendingAdds,
        })
        setVatReviewRows(reviewRows)
        setError(null)
        setVatReviewError(null)
        setPendingVatSubmit({
          qtyOverride: effectiveQtyByLine,
          pendingAddsOverride: effectivePendingAdds,
          options: {
            ...options,
            skipDsdGate: true,
            skipVatReview: true,
          },
        })
        setVatReviewOpen(true)
        return false
      }

      // After VAT review Continue, keep failures on that dialog (last step).
      const surfaceOnVatReview = skipVatReview || vatReviewOpen
      const reportSubmitError = (message) => {
        if (surfaceOnVatReview) {
          setVatReviewError(message)
          setError(null)
        } else {
          setError(message)
          setVatReviewError(null)
        }
      }

      setSubmitting(true)
      setError(null)
      setVatReviewError(null)
      try {
        const lineItems = lines
          .map((li) => ({
            order_line_item_id: li.id,
            quantity: Number(effectiveQtyByLine[li.id]) || 0,
          }))
          .filter((i) => i.quantity > 0)

        const newSkuItems = []
        if (canAddCatalogProduct) {
          for (const add of effectivePendingAdds) {
            const qty = Number(add.quantity) || 0
            const unitPrice = Number(add.unit_price)
            const mrp = Number(add.mrp)
            if (qty <= 0) continue
            const priceError = validatePositiveSellingPriceAndMrp(
              unitPrice,
              mrp,
              {
                requireBoth: true,
              },
            )
            if (priceError) {
              reportSubmitError(
                `${add.product_name || 'Added product'}: ${priceError}`,
              )
              setSubmitting(false)
              return false
            }
            newSkuItems.push({
              admin_product_id: add.admin_product_id,
              quantity: qty,
              unit_price: unitPrice,
              mrp,
            })
          }
        } else if (effectivePendingAdds.length > 0) {
          reportSubmitError(
            'Only Operations Executive, Operations Manager, or Superuser can add products not on the original order.',
          )
          setSubmitting(false)
          return false
        }

        const items = [...lineItems, ...newSkuItems]
        if (items.length === 0) {
          reportSubmitError('Enter at least one quantity greater than zero.')
          setSubmitting(false)
          return false
        }

        const refs = shipmentReferencePayload(referenceFields)
        const locNum = Number(locationId)
        const dispatchOptions = {
          ...shipmentAcknowledgeOptions({
            needsAutoProvisionAck: effectiveNeedsAutoProvisionAck,
            acknowledged,
          }),
          ...(manualSo.active
            ? { soNumber: manualSo.soNumber, reason: manualSo.reason }
            : {}),
        }
        const shipFn =
          order.order_status === 'READY_FOR_DISPATCH'
            ? adminOrderService.dispatchOrder
            : adminOrderService.createShipment

        await shipFn(order.order_id, items, refs, locNum, dispatchOptions)
        setVatReviewOpen(false)
        setVatReviewError(null)
        setPendingVatSubmit(null)
        onSuccess?.()
        onClose()
        return true
      } catch (err) {
        reportSubmitError(parseShipmentError(err))
        return false
      } finally {
        setSubmitting(false)
      }
    },
    [
      order?.order_id,
      order?.order_status,
      submitting,
      qtyByLine,
      locationId,
      availabilityLoading,
      lines,
      availabilityByLine,
      pendingAdds,
      vatReviewOpen,
      availabilityByProduct,
      hasMissingLots,
      missingLotPendingAdds.length,
      acknowledged,
      canAddCatalogProduct,
      referenceFields,
      manualSo.active,
      manualSo.soNumber,
      manualSo.reason,
      dsdByProductId,
      externalIntegrationEnabled,
      onSuccess,
      onClose,
    ],
  )

  const handleVatUpdated = useCallback(
    (productId, vat) => {
      const idKey = String(productId || '').trim()
      if (!idKey) return
      setPendingAdds((prev) =>
        prev.map((add) =>
          String(add.admin_product_id) === idKey ? { ...add, vat } : add,
        ),
      )
      onProductVatUpdated?.(idKey, vat)
    },
    [onProductVatUpdated],
  )

  const handleExcelConfirmCreate = useCallback(
    async ({ updates, catalogAdds }) => {
      if (submitting) return false
      // Excel-only payload from the preview (edited qtys). Do not mutate the
      // manual draft table — normal Ship must keep remaining / manual edits.
      const excelQtyByLine = applyShipmentExcelUpdatesExclusive(lines, updates)
      const excelPendingAdds = canAddCatalogProduct
        ? buildExcelOnlyPendingAdds(catalogAdds)
        : []
      return handleSubmit(excelQtyByLine, excelPendingAdds)
    },
    [submitting, lines, canAddCatalogProduct, handleSubmit],
  )

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {allowManualSo ? (
          <ManualSoNumberFields
            soNumber={soNumber}
            reason={soReason}
            onSoNumberChange={setSoNumber}
            onReasonChange={setSoReason}
            disabled={submitting}
          />
        ) : null}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the source distribution center, then enter quantities for this
          shipment. You may ship more than remaining (order qty will increase)
          {canAddCatalogProduct
            ? ' or add products not on the original order.'
            : '.'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {hasNonPurchasableLine && (
          <NonPurchasableShipmentBanner
            items={nonPurchasableItems}
            disabled={submitting}
            onRemoveAll={handleRemoveAllNonPurchasable}
            onFocusItem={handleFocusNonPurchasableItem}
          />
        )}

        {hasQtyOverRemaining && (
          <Alert severity="info" sx={{ mb: 2 }}>
            One or more ship quantities exceed remaining. Those order lines will be
            increased when you ship.
          </Alert>
        )}

        {canAddCatalogProduct && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              border: '1px solid #eee',
              borderRadius: 2,
              bgcolor: '#fafafa',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Add from catalog
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Search catalog products to include on this shipment. Added items
              appear in the table below.
            </Typography>
            {!orgId ? (
              <Alert severity="warning">
                This order is missing an organisation id, so catalog search is
                unavailable.
              </Alert>
            ) : (
              <Autocomplete
                options={searchOptions}
                loading={searchLoading}
                value={selectedProduct}
                inputValue={searchInput}
                onInputChange={(_e, value, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setSearchInput(value)
                  }
                }}
                onChange={(_e, value) => {
                  setSelectedProduct(value)
                  if (value) tryAddCatalogProduct(value)
                }}
                getOptionLabel={(option) => {
                  if (!option) return ''
                  const name = option.product_name || option.name || 'Product'
                  const code = getArticleCode(option)
                  return code ? `${name} (${code})` : name
                }}
                isOptionEqualToValue={(a, b) =>
                  String(a?.admin_product_id || a?.id || '') ===
                  String(b?.admin_product_id || b?.id || '')
                }
                filterOptions={(x) => x}
                disabled={submitting}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Search products"
                    placeholder="Name, article code, barcode…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Box>
        )}

        <ShipmentExcelImportPanel
          orderLineItems={lines}
          qtyByLine={qtyByLine}
          pendingAdds={pendingAdds}
          orgId={orgId}
          canAddCatalogProduct={canAddCatalogProduct}
          existingProductIds={existingProductIds}
          disabled={!open}
          submitting={submitting}
          resetToken={open ? `${order?.order_id || 'order'}-open` : 'closed'}
          onConfirmCreate={handleExcelConfirmCreate}
          locationId={locationId}
          locationField={
            <DistributionCenterField
              options={locations}
              selected={selectedLocation}
              inputValue={locationInputValue}
              loading={locationsLoading}
              loadingMore={locationsLoadingMore}
              disabled={submitting}
              onSelect={selectLocation}
              onInputChange={handleLocationInputChange}
              onListScroll={handleLocationListScroll}
              elevatePopper
            />
          }
          showNoLocationsWarning={showNoLocationsWarning}
          submitError={error}
          acknowledgementLabel={acknowledgementLabel}
          acknowledged={acknowledged}
          onAcknowledgedChange={setAcknowledged}
          needsAcknowledgement={needsAcknowledgement}
        />

        <DistributionCenterField
          options={locations}
          selected={selectedLocation}
          inputValue={locationInputValue}
          loading={locationsLoading}
          loadingMore={locationsLoadingMore}
          disabled={submitting}
          onSelect={selectLocation}
          onInputChange={handleLocationInputChange}
          onListScroll={handleLocationListScroll}
        />

        {showNoLocationsWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No distribution centers are available.
          </Alert>
        )}

        {locationId &&
          (missingLotLines.length > 0 || missingLotPendingAdds.length > 0) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Missing warehouse lots at selected DC
            </Typography>
            {missingLotLines.map((li) => {
              const row = availabilityByLine[li.id]
              const articleCode = row?.sku || getArticleCode(li)
              return (
                <Typography key={li.id} variant="body2" sx={{ mt: 0.5 }}>
                  {articleCode ? `${articleCode} — ` : ''}
                  {row?.product_name || li.product_name || `Line ${li.id}`}
                  {row?.warning ? `: ${row.warning}` : ''}
                </Typography>
              )
            })}
            {missingLotPendingAdds.map((add) => {
              const row = availabilityByProduct[String(add.admin_product_id)]
              const articleCode = row?.sku || add.sku
              return (
                <Typography key={add.key} variant="body2" sx={{ mt: 0.5 }}>
                  {articleCode ? `${articleCode} — ` : ''}
                  {row?.product_name || add.product_name || 'Added product'}
                  {row?.warning ? `: ${row.warning}` : ''}
                </Typography>
              )
            })}
          </Alert>
        )}

        {acknowledgementLabel && (
          <FormControlLabel
            sx={{ mb: 2, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={submitting || hasNonPurchasableLine}
                inputProps={{ 'aria-label': 'I understand' }}
              />
            }
            label={acknowledgementLabel}
          />
        )}

        <ShipmentReferenceFields
          values={referenceFields}
          onChange={setReferenceFields}
          disabled={submitting}
          showHeading
          description="Optional delivery note (DN), TPN, and invoice number for this shipment."
        />

        <TextField
          size="small"
          fullWidth
          label="Search lines"
          placeholder="Article code or product name"
          value={listFilter}
          onChange={(e) => {
            setListFilter(e.target.value)
            setListPage(0)
          }}
          sx={{ mt: 2, mb: 1 }}
        />

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Ordered</TableCell>
                <TableCell align="right">Shipped</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell align="right">Available</TableCell>
                <TableCell align="right">MRP</TableCell>
                <TableCell align="right">Selling Price</TableCell>
                <TableCell align="center">DSD</TableCell>
                <TableCell
                  align="right"
                  sx={{ width: 112, minWidth: 112, whiteSpace: 'nowrap' }}
                >
                  Ship now
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedProductRows.map((entry) => {
                if (entry.kind === 'pending') {
                  const add = entry.add
                  return (
                    <PendingAddRow
                      key={add.key}
                      add={add}
                      locationId={locationId}
                      availabilityLoading={availabilityLoading}
                      availRow={
                        availabilityByProduct[String(add.admin_product_id)]
                      }
                      submitting={submitting}
                      onQtyChange={(next) => {
                        setPendingAdds((prev) =>
                          prev.map((row) =>
                            row.key === add.key
                              ? { ...row, quantity: next === '' ? '' : next }
                              : row,
                          ),
                        )
                      }}
                      onSellingPriceChange={(v) => {
                        setPendingAdds((prev) =>
                          prev.map((row) =>
                            row.key === add.key ? { ...row, unit_price: v } : row,
                          ),
                        )
                      }}
                      onRemove={() =>
                        setPendingAdds((prev) =>
                          prev.filter((row) => row.key !== add.key),
                        )
                      }
                    />
                  )
                }
                const li = entry.li
                return (
                  <ShipmentLineRow
                    key={li.id}
                    lineItem={li}
                    qty={qtyByLine[li.id]}
                    locationId={locationId}
                    availabilityLoading={availabilityLoading}
                    availRow={availabilityByLine[li.id]}
                    onQtyChange={(next) => handleQtyChange(li.id, next)}
                    onQtyBlur={() => handleQtyBlur(li.id)}
                    dsdEnabled={isProductDsdEnabled(li, dsdByProductId)}
                    dsdSaving={
                      dsdSavingProductId === getOrderLineProductId(li)
                    }
                    onDsdToggle={(checked) => handleDsdToggle(li, checked)}
                  />
                )
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredProductRows.length}
            page={listPage}
            onPageChange={(_e, nextPage) => setListPage(nextPage)}
            rowsPerPage={listRowsPerPage}
            onRowsPerPageChange={(e) => {
              setListRowsPerPage(parseInt(e.target.value, 10))
              setListPage(0)
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      </DialogContent>

      <DialogActions
        sx={{
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 160 }}>
          {hasNonPurchasableLine ? (
            <Typography variant="caption" color="error.main">
              Assign is blocked until non-purchasable articles are removed (see
              warning above).
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSubmit()}
            disabled={!canSubmit}
          >
            {submitting ? <CircularProgress size={22} /> : 'Assign'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>

      <Dialog
        open={dsdConfirmOpen}
        onClose={() => {
          if (submitting) return
          setDsdConfirmOpen(false)
          setPendingSubmitOverrides(null)
        }}
      >
        <DialogTitle>DSD products require a separate Fulfillment Center</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            One or more selected products are configured for Direct Store Delivery
            (DSD). Create a dedicated Fulfillment Center assignment for those
            products, or continue with a regular assignment. If you continue with
            regular, all DSD product quantities will be set to 0 (product DSD
            settings are not changed).
          </Typography>
          {(() => {
            const selection = collectPositiveDsdSelections({
              lines,
              qtyByLine: pendingSubmitOverrides?.qtyByLine || qtyByLine,
              pendingAdds:
                pendingSubmitOverrides?.pendingAdds != null
                  ? pendingSubmitOverrides.pendingAdds
                  : pendingAdds,
              dsdByProductId,
            })
            const names = [
              ...selection.lineHits.map((h) => h.label),
              ...selection.pendingHits.map((h) => h.label),
            ]
            if (!names.length) return null
            return (
              <Alert severity="info" sx={{ mt: 1 }}>
                DSD products with quantity selected:{' '}
                {Array.from(new Set(names)).join(', ')}
              </Alert>
            )
          })()}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => {
              setDsdConfirmOpen(false)
              setPendingSubmitOverrides(null)
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            disabled={submitting}
            onClick={async () => {
              const baseQty =
                pendingSubmitOverrides?.qtyByLine || qtyByLine
              const basePending =
                pendingSubmitOverrides?.pendingAdds != null
                  ? pendingSubmitOverrides.pendingAdds
                  : pendingAdds
              const cleared = zeroDsdQuantities({
                lines,
                qtyByLine: baseQty,
                pendingAdds: basePending,
                dsdByProductId,
              })
              setQtyByLine(cleared.qtyByLine)
              setPendingAdds(cleared.pendingAdds)
              setDsdConfirmOpen(false)
              setPendingSubmitOverrides(null)

              const stillDsd = collectPositiveDsdSelections({
                lines,
                qtyByLine: cleared.qtyByLine,
                pendingAdds: cleared.pendingAdds,
                dsdByProductId,
              })
              if (stillDsd.hasDsd) {
                setError(
                  `Clear DSD quantities before a regular shipment: ${[
                    ...stillDsd.lineHits,
                    ...stillDsd.pendingHits,
                  ]
                    .map((h) => h.label)
                    .join(', ')}`,
                )
                return
              }
              const hasAny =
                lines.some((li) => (Number(cleared.qtyByLine[li.id]) || 0) > 0) ||
                cleared.pendingAdds.some((a) => (Number(a.quantity) || 0) > 0)
              if (!hasAny) {
                setError(
                  'All selected quantities were DSD products. Create a Fulfillment Center assignment for them, or select non-DSD quantities for a regular assignment.',
                )
                return
              }
              await handleSubmit(
                cleared.qtyByLine,
                cleared.pendingAdds,
                { skipDsdGate: true },
              )
            }}
          >
            Continue with regular
          </Button>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={() => {
              setDsdConfirmOpen(false)
              setDsdDialogOpen(true)
            }}
          >
            Proceed with DSD Fulfillment Center
          </Button>
        </DialogActions>
      </Dialog>

      <DsdShipmentDialog
        open={dsdDialogOpen}
        onClose={() => {
          setDsdDialogOpen(false)
          setPendingSubmitOverrides(null)
        }}
        order={order}
        canEnterManualSo={allowManualSo}
        // TEMPORARY: SHIPMENT_INTEGRATION_FLAG — REMOVE AFTER GLOBAL ROLLOUT
        // DSD dialog is only reachable when flag is on; still pass for VAT review.
        externalIntegrationEnabled={externalIntegrationEnabled}
        initialLocation={selectedLocation}
        initialSoNumber={soNumber}
        initialSoReason={soReason}
        dsdLines={lines.filter((li) =>
          isProductDsdEnabled(li, dsdByProductId),
        )}
        initialQtyByLine={
          pendingSubmitOverrides?.qtyByLine || qtyByLine
        }
        initialPendingAdds={(
          pendingSubmitOverrides?.pendingAdds != null
            ? pendingSubmitOverrides.pendingAdds
            : pendingAdds
        ).filter((add) => {
          const productId = String(add.admin_product_id || '').trim()
          if (
            productId &&
            Object.prototype.hasOwnProperty.call(dsdByProductId, productId)
          ) {
            return dsdByProductId[productId] === true
          }
          return add.dsd === true
        })}
        onProductVatUpdated={handleVatUpdated}
        onSuccess={onSuccess}
      />

      <ShipmentVatReviewDialog
        open={vatReviewOpen}
        onClose={() => {
          if (submitting) return
          setVatReviewOpen(false)
          setVatReviewError(null)
          setPendingVatSubmit(null)
        }}
        rows={vatReviewRows}
        submitting={submitting}
        submitError={vatReviewError}
        onVatUpdated={handleVatUpdated}
        onConfirm={async () => {
          if (!pendingVatSubmit) return
          const { qtyOverride, pendingAddsOverride, options } = pendingVatSubmit
          const ok = await handleSubmit(
            qtyOverride,
            pendingAddsOverride,
            options,
          )
          if (ok) {
            setVatReviewOpen(false)
            setVatReviewError(null)
            setPendingVatSubmit(null)
          }
        }}
      />
    </>
  )
}

export default CreateShipmentModal
